// RelatedWishesPanel — cross-module reference for AnniversaryDetail
// (W35.2 per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §3.2).
//
// Shows pair wishes whose `target_date` is within ±30 days of the
// anniversary's `date`. Pure client-side filter over the existing
// /api/v1/wishes?limit=50 list — no new backend endpoint.
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~3KB).

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarHeart, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { listWishes, type WishSummary } from "@/lib/api/wishlist";
import { wishStatusLabel } from "@/lib/i18n/wishlist";
import { cn } from "@/lib/utils/cn";

export interface RelatedWishesPanelProps {
  /** Anniversary date in YYYY-MM-DD format. */
  anniversaryDate: string;
  className?: string;
}

/** Returns YYYY-MM-DD for the date offset by `days` (HKT). */
function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function RelatedWishesPanel({
  anniversaryDate,
  className,
}: RelatedWishesPanelProps) {
  const { data } = useQuery({
    queryKey: ["wishes", "list", "related", anniversaryDate],
    queryFn: () => listWishes({ limit: 50 }),
    enabled: Boolean(anniversaryDate),
    staleTime: 60_000,
  });

  const all: WishSummary[] = data?.items ?? [];
  const from = shiftDate(anniversaryDate, -30);
  const to = shiftDate(anniversaryDate, 30);
  const related = all.filter(
    (w) => w.target_date && w.target_date >= from && w.target_date <= to,
  );

  if (related.length === 0) {
    return null;
  }

  return (
    <Card
      data-component="RelatedWishesPanel"
      data-testid="related-wishes-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center gap-2 mb-s-2">
        <CalendarHeart className="h-4 w-4" aria-hidden />
        <h3 className="font-display text-h3 text-ink-1">相關心願</h3>
        <span className="text-meta text-ink-3 ml-1">
          （{anniversaryDate} ± 30 天）
        </span>
      </header>
      <ol className="flex flex-col gap-s-2">
        {related.map((w) => (
          <li
            key={w.id}
            data-testid={`related-wish-row-${w.id}`}
            className="flex items-center gap-2"
          >
            <Link
              to={`/wishes/${w.id}`}
              data-testid={`related-wish-link-${w.id}`}
              className="press flex-1 flex items-center justify-between gap-2 p-s-3 rounded-md bg-paper-deep hover:bg-paper-calm text-ink-1"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-body-sm truncate">{w.name}</p>
                <p className="text-meta text-ink-3 tabular-nums">
                  {/* W53.1 D-6: rendered the raw enum — a 繁體中文 screen with
                      the literal string `pending` on it. This file was not in
                      the spec's six-consumer inventory; guard G-2 found it,
                      which is what the guard is for. */}
                  目標 {w.target_date} · {wishStatusLabel(w.status)}
                </p>
              </div>
              <ChevronRight
                className="h-4 w-4 text-ink-3 shrink-0"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}